import { useQuery, useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { type ReactNode, useRef } from "react";
import { getInitialConnectorValues } from "../../shared/connectors/getInitialConnectorValues";
import { isDraft } from "../../shared/connectors/isDraft";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useConnectorSelectOnSave } from "../hooks/useConnectorSelectOnSave";
import { useDefaultConnectorGrants } from "../hooks/useDefaultConnectorGrants";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsMobile } from "../hooks/useIsMobile";
import { connectorDirectory } from "../lib/connectorDirectory";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { useRenderWithPlaceholders } from "./ConnectorModal/renderWithPlaceholders";
import {
  type ConnectedAvatar,
  ConnectorAvatars,
  type DisconnectedAvatar,
} from "./ConnectorAvatars";
import {
  SearchableDropdown,
  RowButton,
  SectionLabel,
} from "./SearchableDropdown";
import { type AppInfo, ConnectorOption, ConnectorRow } from "./ConnectorRow";
import { ClickButton } from "./ClickButton";
import {
  type AvailableItem,
  type ConnectedItem,
  ConnectorSelectorDropdown,
  type DisconnectedItem,
} from "./ConnectorSelectorDropdown";
import { Icon } from "./Icon";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { useMakeUrl, navigate, routes } from "../lib/urls";

// A connector the target references but can't currently resolve (missing or
// unreadable): rendered faded with reconnect/create options.
type DisconnectedSlot = { id: string; appType: string; slug?: string };

type Props = {
  // Noun used in labels, e.g. "step" or "chat".
  parentNoun: string;
  canWrite: boolean;
  disabled?: boolean;
  // The space new connectors are shared with; omitted for personal chats.
  spaceId?: string;
  connected: readonly (AppInfo & {
    section?: string;
    removable?: boolean;
  })[];
  // Ids already attached to the target — excluded from the "available" list.
  excludeIds: ReadonlySet<string>;
  // When set, the "available" list is restricted to these connector ids (e.g.
  // only the ones the current space can use).
  availableIds?: ReadonlySet<string>;
  disconnected?: readonly DisconnectedSlot[];
  // May return a promise when associating requires navigation (branch switch)
  // that must settle before a freshly created app is opened.
  associate: (connectedAppId: string, slug?: string) => unknown;
  remove: (connectedAppId: string) => void;
  onManage?: () => void;
  // Run before any add mutation, e.g. a chat lazily creating itself.
  beforeMutate?: () => void;
  // Run after a newly created app is associated; defaults to opening its modal.
  afterCreate?: (connectedAppId: string) => void;
  // Gap in px between the opener/avatar and its menu (default 4).
  menuMargin?: number;
  subtleAddButton?: boolean;
  vertical?: boolean;
  avatarSize?: 24 | 32;
};

export function ConnectorsBar({
  parentNoun,
  canWrite,
  disabled = false,
  spaceId,
  connected,
  excludeIds,
  availableIds,
  disconnected = [],
  associate,
  remove,
  onManage,
  beforeMutate,
  afterCreate,
  menuMargin = 4,
  subtleAddButton = false,
  vertical = false,
  avatarSize = 24,
}: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const isMobile = useIsMobile();
  const menuSide = vertical ? (isMobile ? "right" : "left") : undefined;
  const renderWithPlaceholders = useRenderWithPlaceholders();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const defaultConnectorGrants = useDefaultConnectorGrants(spaceId);
  const [personalSpace] = useQuery(queries.personalSpace());
  // The UI never calls a personal space a "space".
  const availableLabel =
    spaceId && spaceId !== personalSpace?.id
      ? "Available to this space"
      : "Available to you";

  const [allApps, appsResult] = useQuery(queries.connectorsIndex());

  const ready = !disabled && appsResult.type === "complete";

  const available = allApps.filter(
    (app) =>
      !excludeIds.has(app.id) &&
      !isDraft(app) &&
      (availableIds ? availableIds.has(app.id) : true)
  );

  const resolveIdentifier = (appType: string) =>
    connectorDirectory.find(
      (a) => a.identifier.toLowerCase() === appType.toLowerCase()
    )?.identifier ?? appType;

  const add = (connectedAppId: string, slug?: string) => {
    if (!ready || !canWrite) return;
    beforeMutate?.();
    return associate(connectedAppId, slug);
  };

  const removeConnector = (id: string) => {
    if (!ready || !canWrite) return;
    remove(id);
  };

  const pendingSlugRef = useRef<string | undefined>(undefined);
  const associateOnSave = useConnectorSelectOnSave((connector) => {
    if (canWrite) void associate(connector.id, pendingSlugRef.current);
  });

  const createApp = (appType: string, slug?: string) => {
    if (!ready || !canWrite || !canCreateApp) return;
    const identifier = resolveIdentifier(appType);
    const appDef = connectorDirectory.find((a) => a.identifier === identifier);
    if (!appDef || !defaultConnectorGrants) return;
    beforeMutate?.();
    const initialValues = getInitialConnectorValues(
      appDef.definition,
      renderWithPlaceholders
    );
    const newAppId = nanoid(12);
    z.mutate(
      sharedMutators.createConnectedApp({
        connectedAppId: newAppId,
        name: initialValues.name || appDef.name,
        identifier,
        urlPattern: initialValues.urlPattern,
        configuration: initialValues.configuration,
        secrets: {},
        createOAuthConnection: initialValues.createOAuthConnection,
        grants: defaultConnectorGrants,
      })
    );
    pendingSlugRef.current = slug;
    associateOnSave(newAppId);
    if (afterCreate) afterCreate(newAppId);
    else navigate(routes.modal.connector, { id: newAppId });
  };

  const connectedChips: ConnectedItem[] = connected.map((app) => ({
    ...app,
    manageTo: makeUrl(routes.modal.connector, { id: app.id }),
    onManage,
    onRemove:
      canWrite && app.removable !== false
        ? () => removeConnector(app.id)
        : undefined,
  }));

  const availableChips: AvailableItem[] = available.map((app) => ({
    ...app,
    onSelect: () => void add(app.id),
  }));

  const disconnectedChips: DisconnectedItem[] = disconnected.map((slot) => {
    const identifier = resolveIdentifier(slot.appType);
    return {
      id: slot.id,
      appType: identifier,
      slug: slot.slug,
      options: available.filter((a) => a.identifier === identifier),
      onSelect: (connectedAppId: string) => void add(connectedAppId, slot.slug),
      onCreateNew:
        canWrite && canCreateApp
          ? () => createApp(slot.appType, slot.slug)
          : undefined,
    };
  });

  const connectedAvatars: ConnectedAvatar[] = connected.map((app) => ({
    id: app.id,
    name: app.name,
    app,
    onRemove:
      app.removable !== false ? () => removeConnector(app.id) : undefined,
    removeDisabled: !canWrite,
  }));

  const disconnectedAvatars: DisconnectedAvatar[] = disconnectedChips.map(
    (item) => {
      const appDef = connectorDirectory.find(
        (a) => a.identifier === item.appType
      );
      const name = appDef?.name ?? item.appType;
      return {
        id: item.id,
        appType: item.appType,
        slug: item.slug,
        // Nothing to pick from — skip the dropdown and go straight to create.
        wrap: (opener) =>
          item.options.length === 0 ? (
            <ClickButton
              disabled={!ready || !canWrite || !item.onCreateNew}
              onClick={() => item.onCreateNew?.()}
            >
              {opener}
            </ClickButton>
          ) : (
            <SearchableDropdown
              searchable={false}
              disabled={!ready || !canWrite}
              opener={opener}
              side={menuSide}
              margin={menuMargin}
            >
              {(_query, close) => (
                <>
                  <SectionLabel>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Use an existing connector
                    </Text>
                  </SectionLabel>
                  {item.options.map((opt) => (
                    <ConnectorOption
                      key={opt.id}
                      app={opt}
                      onClick={() => item.onSelect(opt.id)}
                      close={close}
                    />
                  ))}
                  {item.onCreateNew && (
                    <>
                      <SectionLabel>
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          Add new
                        </Text>
                      </SectionLabel>
                      <RowButton
                        onClick={() => {
                          item.onCreateNew?.();
                          close();
                        }}
                      >
                        <ConnectorRow
                          template={{ name, identifier: item.appType }}
                          name={name}
                          secondary={appDef?.tagLine}
                        />
                      </RowButton>
                    </>
                  )}
                </>
              )}
            </SearchableDropdown>
          ),
      };
    }
  );

  const hasPile = connectedAvatars.length + disconnectedAvatars.length > 0;

  const connectorMenu = (
    opener: ReactNode,
    props: Pick<
      React.ComponentProps<typeof ConnectorSelectorDropdown>,
      "aria-label" | "style"
    >
  ) => (
    <ConnectorSelectorDropdown
      {...props}
      disabled={!canWrite || !ready}
      parentNoun={parentNoun}
      margin={menuMargin}
      side={menuSide}
      opener={opener}
      connected={connectedChips}
      available={availableChips}
      availableLabel={availableLabel}
      disconnected={disconnectedChips}
      onCreateApp={
        canCreateApp ? (identifier) => createApp(identifier) : undefined
      }
    />
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: vertical ? "column-reverse" : "row",
        alignItems: "center",
        gap: 6,
      }}
    >
      <ConnectorAvatars
        disabled={!ready}
        connected={connectedAvatars}
        disconnected={disconnectedAvatars}
        menu={{ parentNoun, margin: menuMargin }}
        vertical={vertical}
        itemSize={avatarSize}
        wrapOverflow={
          canWrite && ready
            ? (badge) =>
                connectorMenu(badge, {
                  "aria-label": `All connectors on this ${parentNoun}`,
                })
            : undefined
        }
      />
      {connectorMenu(
        <Tooltip
          side={menuSide}
          content={
            canWrite
              ? "Add connector"
              : "You don’t have permission to add connectors"
          }
        >
          <div
            style={canWrite ? undefined : { opacity: 0.5, cursor: "default" }}
          >
            <ConnectorLogoContainer
              size={avatarSize}
              borderRadius={avatarSize / 2}
              outlineWidth={2}
              subtleHover={subtleAddButton && !hasPile}
            >
              <Icon
                name={hasPile ? "plus" : "api-key-custom"}
                size={avatarSize === 32 ? 16 : 12}
              />
            </ConnectorLogoContainer>
          </div>
        </Tooltip>,
        {
          "aria-label": `Connectors on this ${parentNoun}`,
          style: {
            marginLeft: hasPile && !vertical ? -12 : 0,
            marginBottom: hasPile && vertical ? -12 : 0,
            zIndex: 10,
          },
        }
      )}
    </div>
  );
}
